import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import moment from 'moment'
import { useToasts } from 'react-toast-notifications'

import { P } from '../../../../style/Typography.style'

import Table from '../../../../components/Table'
import Flex from '../../../../components/layouts/Flex'
import { FilterCheckBox } from '../../../../components/dashboard/FilterInput'
import TableAction from './TableAction'

import axiosInstance from '../../../../services/config/AxiosIntance'

import useBulkActions from '../hooks/useBulkActions'

import {
  setInterviews,
  setApplicantListUrl,
  setUpdatedStage,
} from '../../../../store/actions/recruitersActions'

const FirstInterview = ({ onRowClick }) => {
  const { addToast } = useToasts()
  const { filters, updatedStage, interviews, refreshList, changedInterviews } =
    useSelector((state) => state.requisitions)

  const {
    getCheckedResult,
    handleCheckColumn,
    handleCheckRow,
    selectedAll,
    checkedItems,
  } = useBulkActions()

  const dispatch = useDispatch()
  const [totalPage, setTotalPage] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(true)
  const [rowCount, setRowCount] = useState(0)

  const currentUser = localStorage.getItem('user')
  const role = JSON.parse(currentUser)?.user_type
  const userId = JSON.parse(currentUser)?.id
  const recruiterAssistant = !!JSON.parse(currentUser)?.recruiter_assistant

  const companyDetails =
    role === 'poc' && JSON.parse(currentUser)?.companyDetails

  const stage = 'first_interview'
  let url = `/interviews?stage=${stage}&searchTerm=${filters.search}&job_title=${filters.searchRole}&vacancy=${filters.vacancy}&startDate=${filters.startDate}&endDate=${filters.endDate}&sort=${filters.sort}&readBy=${userId}&smartSearchId=${filters.smartSearchId}`

  if (!recruiterAssistant && role === 'recruiter') {
    url += `&recruiterId=${userId}`
  } else if (role === 'poc') {
    url += `&companyDetails=${JSON.stringify(companyDetails)}`
  }

  const fetchData = useCallback(async (url) => {
    setLoading(true)
    try {
      const result = await axiosInstance.get(url)

      const { data } = result

      dispatch(setApplicantListUrl(url))

      setTotalPage(data?.resp?.pagination?.pageCount || 1)
      setCurrentPage(data?.resp?.pagination?.page || 1)
      setPageSize(data?.resp?.pagination?.pageSize || 10)
      setRowCount(data?.resp?.pagination?.rowCount || 0)

      dispatch(setInterviews(data?.resp?.interviews || []))
      setLoading(false)
    } catch (err) {
      addToast(err?.response?.data?.message, {
        appearance: 'error',
        autoDismiss: true,
      })
      setLoading(false)
    }
    dispatch(setUpdatedStage(''))
  }, [])

  useEffect(() => {
    if (interviews.length && changedInterviews.length) {
      const updatedInterviews = interviews.map((item) => {
        const replacement = changedInterviews.find((t) => t.id === item.id)
        return replacement || item
      })

      dispatch(setInterviews(updatedInterviews))
    }
  }, [changedInterviews])

  useEffect(() => {
    fetchData(url)
  }, [filters])

  useEffect(() => {
    if (updatedStage === 'first_interview') fetchData(url)
  }, [updatedStage])

  useEffect(() => {
    if (updatedStage === 'first_interview' && refreshList === updatedStage) {
      fetchData(url)
    }
  }, [refreshList])

  const columns = React.useMemo(
    () =>
      [
        role !== 'sales' && {
          Header: (
            <FilterCheckBox
              checked={selectedAll}
              mr="0rem"
              size="small"
              justifyContent="center"
              onClick={(e) => handleCheckColumn(e, rowCount)}
            />
          ),
          width: 100,
          accessor: 'checkable',
          disableSortBy: true,
          Cell: ({ value }) => {
            return (
              <FilterCheckBox
                checked={getCheckedResult(value)}
                mr="0rem"
                size="small"
                justifyContent="center"
                onClick={(e) => handleCheckRow(e, value)}
              />
            )
          },
        },
        role !== 'sales' && {
          Header: 'Action',
          accessor: 'actions',
          disableSortBy: true,
          Cell: ({ value }) => <TableAction value={value} />,
        },
        {
          Header: 'Applicant',
          accessor: 'applicantName',
        },
        {
          Header: 'Expected Salary',
          accessor: 'salaryRange',
        },
        {
          Header: 'Role',
          accessor: 'role',
        },
        {
          Header: 'Email',
          accessor: 'email',
        },
        {
          Header: 'Client',
          accessor: 'client',
        },
        {
          Header: 'Contact No.',
          accessor: 'contact',
        },
        {
          Header: 'Schedule',
          accessor: 'schedule',

          Cell: ({ row }) => {
            const meetUrl = row?.original?.meetUrl
            const schedule = (
              <>
                <StyledRow>
                  <p>{row?.original?.interviewDate}</p>
                  <p>{row?.original?.interviewTime}</p>
                </StyledRow>
              </>
            )
            let content = (
              <StyledURLRow href={meetUrl} rel="noreferrer" target="_blank">
                {schedule}
              </StyledURLRow>
            )

            if (!meetUrl) {
              content = schedule

              if (row?.original?.interviewDate === 'Invalid date') {
                content = <StyledRow>Interview date not yet set</StyledRow>
              }
            }
            return content
          },
        },
        {
          Header: 'Recruiter',
          accessor: 'recruiter',
        },
        {
          Header: 'Channel',
          accessor: 'channel',
        },
        {
          Header: 'Asking Salary',
          accessor: 'askingSalary',
        },
        {
          Header: 'Date Created',
          accessor: 'dateCreated',
          Cell: ({ value }) =>
            value ? moment(value).format('DD/MM/YYYY') : '',
          sortType: 'datetime',
        },
        {
          Header: 'Last Updated',
          accessor: 'lastUpdated',
          Cell: ({ value }) =>
            value ? moment(value).format('DD/MM/YYYY') : '',
          sortType: 'datetime',
        },
      ].filter(Boolean),
    [selectedAll, checkedItems, rowCount, role]
  )

  const tableData = interviews.map(
    (item) =>
      ({
        checkable: item.id,
        readApplicantId: item.readApplicantId,
        actions: item,
        applicantName: item.applicantName || '',
        applicantMinSalary: item.applicantMinSalary,
        applicantMaxSalary: item.applicantMaxSalary,
        salaryRange: `${item.applicantCurrency || ''}  ${
          item.applicantMinSalary || '0'
        } - ${item.applicantMaxSalary || '0'}`,
        role: item.role || '',
        email: item.applicantEmail || '',
        client: item.client || '',
        contact: item.mobileNumber || '',
        recruiter: item.recruiterName || '',
        interviewer: item.interviewer || item.interviewerRecruiter || '',
        jobStatus: item.jobStatus || '',
        meetUrl: item.meet_url,
        interviewDate: moment(item.start_time).format('MMMM DD, YYYY'),
        interviewTime:
          moment(item.start_time).format('hh:mm a') +
          ' - ' +
          moment(item.end_time).format('hh:mm a'),
        addedBy: item.added_by || '',
        channel: item.channel || '',
        askingSalary: item.askingSalary || '',
        dateCreated: item.created_at ? new Date(item.created_at) : null,
        lastUpdated: item.updated_at ? new Date(item.updated_at) : null,
      } || '')
  )

  return (
    <StyledFirstInterview>
      {interviews?.length > 0 ? (
        <Table
          data={tableData}
          columns={columns}
          loading={loading}
          totalPage={totalPage}
          currentPage={currentPage}
          pageSize={pageSize}
          rowCount={rowCount}
          fetchData={fetchData}
          setNewPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          url={`${url}&pageNumber=${currentPage}&pageSize=${pageSize}`}
          noData="First Interview is empty"
          height="calc(100vh - 20.3rem)"
          onClick={onRowClick}
        />
      ) : (
        <Flex
          height="calc(100vh - 15rem)"
          bg="#fff"
          justify="center"
          items="center"
        >
          <P>First Interview is empty</P>
        </Flex>
      )}
    </StyledFirstInterview>
  )
}

export default FirstInterview

const StyledFirstInterview = styled.div`
  width: 100%;
  height: 100%;
`
const StyledURLRow = styled.a`
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
`

const StyledRow = styled.div`
  font-weight: 500;
  font-size: 14px;

  p {
    margin: 0;
  }
`
